import React, { createRef, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import useOnScreen from '../useOnScreen'
import constants from '../../../common/constants'

const types = {
  click: 'click',
  impression: 'impression',
  view: 'view',
  hidden: 'hidden',
}

const AdvertisingSlot = ({
  children,
  id,
  slotId,
  onClick,
  onView,
  onVisible,
  onImpression,
  advisor,
  style,
  className,
  dataTestId,
}) => {
  const timeout = createRef()
  const [impression, setImpression] = useState(true)
  const [view, setView] = useState(true)
  const reference = createRef()
  const visible = useOnScreen(reference)

  useEffect(() => {
    if (typeof onVisible === 'function' && visible === true) {
      onVisible(advisor)
    }
  }, [visible])

  // Impression
  useEffect(() => {
    if (impression && onImpression) {
      onImpression({
        id,
        slotId,
        type: types.impression,
        eventId: advisor.eventId,
        adUrl: advisor.url,
        isAdvisor: true,
      })
      setImpression(false)
    }
  }, [])

  // View
  useEffect(() => {
    if (visible !== null && onView) {
      const startTime = Date.now()
      timeout.current = setTimeout(() => {
        if (visible && view) {
          onView({
            id,
            slotId,
            type: types.view,
            startTime,
            endTime: Date.now(),
            isAdvisor: true,
          })
          setView(false)
        }
      }, constants.ADVISOR_VIEW_EVENT_DELAY)
    }
    return () => {
      if (timeout.current) clearTimeout(timeout.current)
    }
  }, [visible])

  // Click
  const handleClick = (event) => {
    event.preventDefault()
    const isBlank = event.ctrlKey || event.metaKey

    if (onClick && advisor.redirectUrl) {
      onClick({
        id,
        slotId,
        visible,
        eventId: advisor.eventId,
        type: types.click,
        adUrl: advisor.url,
        endTime: Date.now(),
        redirectUrl: advisor.redirectUrl,
        isAdvisor: true,
        isBlank,
      })
    }
  }

  const href = advisor.redirectUrl ? `${advisor.redirectUrl}` : window.location.pathname

  return (
    <a
      id={id}
      ref={reference}
      href={href}
      onClick={handleClick}
      style={style}
      className={className}
      data-testid={dataTestId}
    >
      {children}
    </a>
  )
}

AdvertisingSlot.propTypes = {
  children: PropTypes.node.isRequired,
  id: PropTypes.string.isRequired,
  slotId: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
  onVisible: PropTypes.func.isRequired,
  onImpression: PropTypes.func.isRequired,
  advisor: PropTypes.any.isRequired,
  style: PropTypes.object,
  className: PropTypes.string,
  dataTestId: PropTypes.string,
}

AdvertisingSlot.defaultProps = {
  style: undefined,
  className: undefined,
  dataTestId: '',
}

const Wrapper = ({
  advisor,
  isMobile,
  children,
  onClick,
  onView,
  onVisible,
  onImpression,
  extraProps,
  style,
  className,
  single,
  dataTestId,
  isHistoryPush,
}) => {
  const fallbackJsx = (
    <div
      role="presentation"
      onClick={() => onClick({ extraProps, isAdvisor: false })}
      style={style}
      className={className}
    >
      {children}
    </div>
  )

  const fallbackLinkJsx = (
    <a
      id={extraProps.id}
      href={extraProps.link}
      target={extraProps.target}
      onClick={() => onClick({ extraProps, isAdvisor: false })}
      style={style}
      className={className}
    >
      {children}
    </a>
  )

  if (!advisor) {
    return extraProps?.link && !isHistoryPush ? fallbackLinkJsx : fallbackJsx
  }
  let advisorResolve = advisor[isMobile ? 'mobile' : 'desktop']
  if (single) {
    advisorResolve = advisor
  }
  if (!advisorResolve) {
    return extraProps?.link && !isHistoryPush ? fallbackLinkJsx : fallbackJsx
  }

  const onViewHandler = (data) =>
    onView({
      ...data,
      event: types.view,
      eventId: advisorResolve.eventId,
      adUrl: advisorResolve.url,
      extraProps,
    })

  const onVisibleHandler = (data) =>
    onVisible({
      ...data,
      event: types.view,
      eventId: advisorResolve.eventId,
      adUrl: advisorResolve.url,
      extraProps,
    })
  const onImpressionHandler = (data) => onImpression({ ...data, extraProps })
  const onClickHandler = (data) => onClick({ ...data, extraProps })

  return (
    <AdvertisingSlot
      id={advisorResolve.id}
      slotId={advisorResolve.slotId}
      advisor={advisorResolve}
      onClick={onClickHandler}
      onView={onViewHandler}
      onImpression={onImpressionHandler}
      onVisible={onVisibleHandler}
      style={style}
      className={className}
      dataTestId={dataTestId}
    >
      {children}
    </AdvertisingSlot>
  )
}

Wrapper.propTypes = {
  children: PropTypes.node.isRequired,
  isMobile: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
  onVisible: PropTypes.func.isRequired,
  onImpression: PropTypes.func.isRequired,
  advisor: PropTypes.any,
  extraProps: PropTypes.any,
  style: PropTypes.object,
  className: PropTypes.string,
  single: PropTypes.bool,
  dataTestId: PropTypes.string,
  isHistoryPush: PropTypes.bool,
}

Wrapper.defaultProps = {
  extraProps: {},
  style: undefined,
  className: undefined,
  advisor: false,
  single: false,
  dataTestId: 'advertising-slot-test-id',
  isHistoryPush: false,
}

export default Wrapper
